import { useRef, useState } from 'react';
import { BookOpen, X } from 'lucide-react';
import { api, ApiError } from './api';
import './ai-policy-guide.css';
import { AssessmentPolicy, type AssessmentStatus } from './AssessmentPolicy';
import type { Session } from './types';

const groups = {
  allowed: 'Allowed',
  out: 'Not allowed',
  check: 'Check first / grey area',
} as const;
type Group = keyof typeof groups;
type Rule = {
  id: string;
  group: Group;
  reviewedGroup: Group;
  title: string;
  summary: string;
  scope: string;
  detail: string;
  nextStep: string;
  reason: string;
  source: string;
  url: string;
};
type Source = {
  id: string;
  title: string;
  url: string;
  version?: string;
  updatedDate?: string;
  effectiveDate?: string;
  moduleStartFrom?: string;
  reviewedAt: string;
  lastAttemptAt?: string;
  lastSuccessAt?: string;
  lastAutomaticSuccessAt?: string;
  statusText: string;
  automaticCheckNote?: string;
};
type Guide = {
  organisation: string;
  guideId?: string;
  notice: string;
  boundary: string;
  reviewedAt?: string;
  reviewKind?: string;
  scope?: string;
  changes?: string;
  freshnessDays: number;
  tutorQuestion: string;
  sources: Source[];
  rules: Rule[];
};

export function AiPolicyButton({ session, studyId, disabledReason, onAssessmentUpdate, beforeReview }: { session?: Session; studyId?: string; disabledReason?: string; onAssessmentUpdate?: (status: AssessmentStatus) => void; beforeReview?: () => Promise<void> } = {}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<HTMLButtonElement>(null);
  const [guide, setGuide] = useState<Guide | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [assessmentRefresh, setAssessmentRefresh] = useState(0);
  const [filter, setFilter] = useState<Group | 'all'>('all');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  async function load() {
    setAssessmentRefresh(value => value + 1);
    if (busy || disabledReason) return;
    setBusy(true);
    setError('');
    try {
      const next = await api<Guide>(session ? `/account/ai-policy?sessionId=${encodeURIComponent(session.id)}` : studyId ? `/account/ai-policy?studyId=${encodeURIComponent(studyId)}` : '/account/ai-policy');
      if (next.organisation !== guide?.organisation || next.guideId !== guide?.guideId) {
        setExpanded(new Set());
        setFilter('all');
      }
      setGuide(next);
    } catch (e) {
      setError((e as Error).message);
      if (e instanceof ApiError && [401, 403].includes(e.status)) setGuide(null);
    } finally {
      setBusy(false);
    }
  }
  function close() {
    dialog.current?.close();
    opener.current?.focus();
  }
  const rules = (guide?.rules ?? []).map((r) =>
    error
      ? {
          ...r,
          group: 'check' as const,
          reason:
            'This retained guide could not be checked. Confirm current guidance; an earlier restriction may still apply.',
        }
      : r,
  );
  return (
    <>
      <span title={disabledReason}><button
        ref={opener}
        className="button secondary"
        disabled={Boolean(disabledReason)}
        aria-description={disabledReason}
        onClick={() => {
          if (disabledReason) return;
          dialog.current?.showModal();
          void load();
        }}
      >
        <BookOpen size={17} />
        AI policy
      </button></span>
      <dialog
        ref={dialog}
        className="ai-guide"
        aria-labelledby="ai-guide-title"
        onCancel={(e) => {
          e.preventDefault();
          close();
        }}
      >
        <header className="ai-guide-heading">
          <div>
            <h2 id="ai-guide-title">AI policy in plain English</h2>
            <p>
              {guide?.organisation ?? 'Your education organisation'} · {session ? 'Organisation for this assignment' : 'Organisation for these studies'}
            </p>
          </div>
          <button className="ai-guide-close" aria-label="Close AI policy guide" onClick={close}>
            <X size={20} />
          </button>
        </header>
        <div className="ai-guide-filters" aria-label="Policy rule filters">
          {Object.entries({ all: 'All rules', ...groups }).map(([id, label]) => (
            <button
              key={id}
              aria-pressed={filter === id}
              onClick={() => setFilter(id as Group | 'all')}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="ai-guide-scroll" aria-busy={busy}>
          {busy && <p role="status">Loading the latest guide…</p>}
          {error && (
            <p role="alert">
              {error}
              {guide &&
                ' Retained session copy · not a current check. Do not assume an earlier restriction has been lifted.'}
            </p>
          )}
          {guide && (
            <>
              <section className="ai-guide-context" aria-label="About this guide"><h3>About this guide</h3><p className="ai-guide-notice">{guide.notice}</p><p className="ai-guide-boundary">{guide.boundary}</p></section>
              {session?.assessmentPolicyStatus && <AssessmentPolicy session={session} refreshToken={assessmentRefresh} onUpdate={onAssessmentUpdate ?? (() => {})} beforeReview={beforeReview ?? (async () => {})} />}
              {Object.entries(groups)
                .filter(([id]) => filter === 'all' || filter === id)
                .map(([id, label]) => (
                  <section key={id} className={`ai-guide-group ${id}`} aria-label={label}>
                    <h3>
                      {label} · {rules.filter((r) => r.group === id).length}
                    </h3>
                    <div className="ai-guide-rules">
                    {rules
                      .filter((r) => r.group === id)
                      .map((r) => (
                        <div className="ai-guide-rule" key={r.id}>
                          <button
                            className="ai-guide-summary"
                            aria-expanded={expanded.has(r.id)}
                            aria-controls={`ai-rule-${r.id}`}
                            onClick={() =>
                              setExpanded((previous) => {
                                const next = new Set(previous);
                                next.has(r.id) ? next.delete(r.id) : next.add(r.id);
                                return next;
                              })
                            }
                          >
                            <span className="ai-guide-chevron" aria-hidden="true">{expanded.has(r.id) ? '⌄' : '›'}</span>
                            <span className="ai-guide-rule-title">
                              <strong>{r.title}</strong>
                              <span>{r.summary}</span>
                              {r.reason && (
                                <small>Earlier: {groups[r.reviewedGroup]} · Needs checking</small>
                              )}
                              <span className="ai-guide-scope">{r.scope}</span>
                            </span>
                          </button>
                          <div
                            id={`ai-rule-${r.id}`}
                            className="ai-guide-detail"
                            hidden={!expanded.has(r.id)}
                          >
                            {r.reason && <p>{r.reason}</p>}
                            <p>{r.detail}</p>
                            <p>{r.nextStep}</p>
                            <a href={r.url} target="_blank" rel="noopener noreferrer">
                              {r.source} ↗
                            </a>
                          </div>
                        </div>
                      ))}
                    {rules.every((r) => r.group !== id) && <p className="ai-guide-empty">No rules in this group.</p>}
                    </div>
                  </section>
                ))}
              {rules.length > 0 && (
                <>
                  <details className="ai-guide-sources">
                    <summary>Sources, dates and what is still missing</summary>
                    <p>
                      Guide reviewed {guide.reviewedAt} · {guide.reviewKind}
                    </p>
                    <p>{guide.scope}</p>
                    <p>
                      Sources are checked daily; freshness window {guide.freshnessDays} days. Failed
                      checks do not update the last successful check.
                    </p>
                    {guide.sources.map((s) => (
                      <div key={s.id} className="ai-guide-source">
                        <a href={s.url} target="_blank" rel="noopener noreferrer">
                          {s.title} ↗
                        </a>
                        <p>
                          {[
                            s.version && `Version ${s.version}`,
                            s.updatedDate && `Published update: ${s.updatedDate}`,
                            s.effectiveDate && `Effective: ${s.effectiveDate}`,
                            s.moduleStartFrom &&
                              `Register: modules starting from ${s.moduleStartFrom}`,
                            `Manual review: ${s.reviewedAt}`,
                            `Last automatic attempt: ${s.lastAttemptAt ?? 'Not yet attempted'}`,
                            `Last successful capture: ${s.lastSuccessAt ?? 'None'}`,
                            `Last automatic success: ${s.lastAutomaticSuccessAt ?? 'None'}`,
                            s.statusText,
                            s.automaticCheckNote,
                          ]
                            .filter(Boolean)
                            .join('\n')}
                        </p>
                      </div>
                    ))}
                    <p>What changed: {guide.changes}</p>
                  </details>
                  <details className="ai-guide-sources">
                    <summary>Ask your tutor</summary>
                    <p>{guide.tutorQuestion}</p>
                  </details>
                </>
              )}
            </>
          )}
        </div>
        <footer className="ai-guide-footer">
          <button onClick={() => void load()} disabled={busy}>
            Reload guide
          </button>
          <button onClick={close}>Done</button>
        </footer>
      </dialog>
    </>
  );
}
